import { parseConfig } from './config.js';
import { fetchFeed, fetchItem } from './api-client.js';
import { renderSkeleton, renderFeed, renderError, renderModal, renderModalSkeleton, closeModal } from './renderer.js';
import { shareArticle } from './share.js';
import { createSwipeHandler } from './swipe.js';
import { WIDGET_STYLES } from './styles.js';
import type { FeedItem, WidgetConfig } from './types.js';

interface WidgetInstance {
  container: HTMLElement;
  shadowRoot: ShadowRoot;
  config: WidgetConfig;
  destroy(): void;
}

const instances: WidgetInstance[] = [];

/**
 * Inject scoped <style> into a Shadow DOM root.
 */
function injectStyles(root: ShadowRoot): void {
  const style = document.createElement('style');
  style.textContent = WIDGET_STYLES;
  root.appendChild(style);
}

/**
 * Bootstrap a single widget instance: fetch feed, render cards,
 * wire card click → modal → share, attach swipe if slider.
 */
function loadFeed(instance: WidgetInstance): void {
  const { shadowRoot, config } = instance;
  let swipeCleanup: (() => void) | null = null;

  fetchFeed(config.feedUrl, config)
    .then((feed) => {
      const onCardClick = (item: FeedItem) => {
        const onClose = () => closeModal(shadowRoot);

        renderModalSkeleton(shadowRoot, onClose);

        fetchItem(config.feedUrl, item.id, config)
          .then((itemResponse) => {
            const onShare = () => shareArticle(item, shadowRoot);
            renderModal(shadowRoot, itemResponse, onClose, onShare);
          })
          .catch(() => {
            // Show fallback modal with excerpt + link instead of silently closing
            const fallback: import('./types.js').ItemResponse = {
              id: item.id,
              title: item.title,
              link: item.link,
              author: item.author,
              publishedAt: item.publishedAt,
              readingTimeMinutes: item.readingTimeMinutes,
              contentType: 'excerpt',
              content: item.excerpt,
              thumbnail: item.thumbnail,
              categories: item.categories,
            };
            const onShare = () => shareArticle(item, shadowRoot);
            renderModal(shadowRoot, fallback, onClose, onShare);
          });
      };

      renderFeed(shadowRoot, feed, config, onCardClick);

      // Attach swipe handler for slider layout
      if (config.layout === 'slider' || (instance.container.offsetWidth || 0) <= 480) {
        const slider = shadowRoot.querySelector('.rss-slider') as HTMLElement | null;
        if (slider) {
          swipeCleanup = createSwipeHandler(slider, (direction) => {
            const cardWidth = slider.firstElementChild
              ? (slider.firstElementChild as HTMLElement).offsetWidth
              : 0;
            const gap = 20;
            const scrollAmount = cardWidth + gap;
            slider.scrollBy({
              left: direction === 'left' ? scrollAmount : -scrollAmount,
              behavior: 'smooth',
            });
          });
        }
      }
    })
    .catch((err) => {
      console.error('[rss-widget] Feed load failed:', err);
      renderError(shadowRoot);
    });

  // Store swipe cleanup in destroy closure
  instance.destroy = () => {
    swipeCleanup?.();
    // Remove all Shadow DOM content
    while (instance.shadowRoot.firstChild) {
      instance.shadowRoot.removeChild(instance.shadowRoot.firstChild);
    }
    instance.container.removeAttribute('data-rss-initialized');
  };
}

/**
 * Discover all [data-rss-widget] containers and bootstrap independent instances.
 */
export function initAllWidgets(): void {
  const containers = document.querySelectorAll<HTMLElement>('[data-rss-widget]');

  for (const container of containers) {
    // Skip already-initialized containers
    if (container.hasAttribute('data-rss-initialized')) continue;

    const config = parseConfig(container);

    // Skip containers missing feed URL
    if (!config.feedUrl) {
      console.warn('[rss-widget] Missing data-feed-url on container', container);
      continue;
    }

    // Create Shadow DOM
    const shadowRoot = container.attachShadow({ mode: 'open' });

    // Inject scoped styles
    injectStyles(shadowRoot);

    // Set theme attribute on host for CSS :host([data-theme]) selectors
    container.setAttribute('data-theme', config.theme);

    // Mark as initialized
    container.setAttribute('data-rss-initialized', 'true');

    const instance: WidgetInstance = {
      container,
      shadowRoot,
      config,
      destroy: () => {
        // Placeholder — replaced in loadFeed once swipe cleanup is available
        while (shadowRoot.firstChild) {
          shadowRoot.removeChild(shadowRoot.firstChild);
        }
        container.removeAttribute('data-rss-initialized');
      },
    };

    instances.push(instance);

    // Render skeleton immediately for perceived speed
    renderSkeleton(shadowRoot, config);

    // Trigger async feed fetch
    loadFeed(instance);
  }
}

/**
 * Destroy all widget instances — remove listeners, detach Shadow DOM content.
 */
export function destroy(): void {
  for (const instance of instances) {
    instance.destroy();
  }
  instances.length = 0;
}

// Auto-init: run immediately if DOM loaded, otherwise wait for DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllWidgets);
} else {
  initAllWidgets();
}
